import * as React from 'react'
import { Link, replace, useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete'
import TaskAltIcon from '@mui/icons-material/TaskAlt';

import { assetsApi } from '../../api-interface';
import { AssetResponse } from '../../api';
import { Typography, Tooltip, Box, useTheme, Button, Grid } from '@mui/material';
import UnstyledLink from '../../components/common/UnstyledLink/UnstyledLink';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import HelpIcon from '@mui/icons-material/Help';
import AssetInfoChip from './AssetInfoChip';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TenantContext } from '../..';
import { useSnackbar } from 'notistack';
import { GridRowsProp, GridColDef, getGridSingleSelectOperators, GridFilterModel } from '@mui/x-data-grid';
import StyledDataGrid, { stringFilterOperators } from '../../components/common/StyledDataGrid/StyledDataGrid';
import { useState } from 'react';


export async function loader(tenant_id) {
  const assets = await assetsApi(tenant_id).getAssetsAssetsGet();
  const ret = assets.data;
  return ret;
}



const Assets = () => {
  const assets = useLoaderData() as AssetResponse[];
  const tenant_id = React.useContext(TenantContext)
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const navigate = useNavigate();
  console.log(assets);
  const theme = useTheme()
  const today = new Date()
  const totalCompliant = assets.filter((asset) => (asset.complaint == "pass")).length
  const totalWarning = assets.filter((asset) => (asset.complaint == "warning")).length
  const totalNonCompliant = assets.filter((asset) => (asset.complaint == "fail")).length

  const [searchParams, setSearchParams] = useSearchParams();

  const complaintFilter = searchParams.get("compliant");

  // Get complaint filter from search params
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: complaintFilter ? [
      { field: 'outcome', operator: 'is', value: complaintFilter === 'pass' ? 'Compliant' : complaintFilter === 'warning' ? 'Warning' : 'Needs Attention' }
    ] : []
  });

  // Sync filterModel with searchParams
  React.useEffect(() => {
    setFilterModel({
      items: complaintFilter ? [
        { field: 'outcome', operator: 'is', value: complaintFilter === 'pass' ? 'Compliant' : complaintFilter === 'warning' ? 'Warning' : 'Needs Attention' }
      ] : []
    });
  }, [complaintFilter]);

  const assetRow = (assetresponse: AssetResponse) => {
    const asset = assetresponse.asset
    const created = new Date(asset.created)
    const most_recent_validation_date = assetresponse?.most_recent_validation?.inspection_date ? new Date(String(assetresponse.most_recent_validation.inspection_date)) : null
    const next_inspection_due = assetresponse?.most_recent_validation?.next_inspection_due ? new Date(String(assetresponse.most_recent_validation.next_inspection_due)) : null
    let most_recent_outcome = assetresponse?.most_recent_validation?.outcome == "pass" ? "Compliant" : assetresponse?.most_recent_validation?.outcome == "warning" ? "Warning" : "Needs Attention"
    if (next_inspection_due && next_inspection_due < today) {
      most_recent_outcome = "Needs Attention"
    }
    return {
      id: asset.id,
      address_1: asset.address_1,
      address_2: asset.address_2,
      city: asset.city,
      postcode: asset.postcode,
      created: created,
      most_recent_inspection: most_recent_validation_date,
      outcome: most_recent_outcome,
      next_inspection_due: next_inspection_due
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'address_1', headerName: 'Address Line 1', flex: 1, minWidth: 200, headerClassName: 'table-header', filterOperators: stringFilterOperators, renderCell: (params) => {
        return <UnstyledLink to={params.row.id}>{params.value}</UnstyledLink>
      }
    },
    { field: 'address_2', headerName: 'Address Line 2', flex: 1, minWidth: 200, headerClassName: 'table-header', filterOperators: stringFilterOperators },
    { field: 'city', headerName: 'City', flex: 1, minWidth: 150, headerClassName: 'table-header', filterOperators: stringFilterOperators },
    { field: 'postcode', headerName: 'Postcode', flex: 1, minWidth: 100, headerClassName: 'table-header', filterOperators: stringFilterOperators },
    {
      field: 'created', headerName: 'Created', flex: 1, minWidth: 150, type: 'date', headerClassName: 'table-header', renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      }
    },
    {
      field: 'most_recent_inspection', headerName: 'Most Recent LGSR Inspection', type: 'date', flex: 1, minWidth: 170, headerClassName: 'table-header', renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) || <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }}></HelpIcon>
      }
    },
    {
      field: 'outcome', headerName: 'LGSR Outcome', flex: 1, minWidth: 150, headerClassName: 'table-header', type: 'singleSelect', valueOptions: ['Compliant', 'Warning', 'Needs Attention'], filterOperators: getGridSingleSelectOperators(), renderCell: (params) => {
        return params.value === "Compliant" ?
          <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.success.main}><TaskAltIcon fontSize="inherit" /> Compliant</Typography>
          : params.value === "Warning" ? <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.warning.main}><ErrorOutlineIcon fontSize="inherit" /> Warning</Typography>
            : <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.error.main}><ErrorOutlineIcon fontSize="inherit" /> Needs Attention</Typography>
      }, sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const order = ['Compliant', 'Warning', 'Needs Attention'];
        return order.indexOf(v1) - order.indexOf(v2);
      }
    },
    {
      field: 'next_inspection_due',
      headerName: 'Next LGSR Inspection Due',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 150,
      type: 'date',
      renderCell: (params) => {
        const value = params.value;
        const isOverdue = value && value < today;
        return value
          ? (
            <Typography
              sx={{ color: isOverdue ? theme.palette.error.main : undefined, verticalAlign: 'middle', display: 'inline' }}
            >
              {value.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
          )
          : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }} />;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 100,
      getActions: (params) => [
        <UnstyledLink to={params.row.id}>
          <Tooltip title="View Asset Info">
            <AnalyticsIcon sx={{ verticalAlign: 'middle' }} />
          </Tooltip>
        </UnstyledLink>,
        <Tooltip title="Delete asset">
          <DeleteIcon sx={{ ml: 1, verticalAlign: 'middle' }} onClick={async () => {
            if (window.confirm("Are you sure you want to remove this asset? Associated inspections will be dissociated")) {
              try {
                await assetsApi(tenant_id).deleteAssetAssetsAssetIdDelete(params.row.id)
                enqueueSnackbar("Asset deleted", { variant: "success" })
                navigate("")
              } catch (e) {
                enqueueSnackbar("Error deleting asset: " + e, { variant: "error" })
              }
            }
          }} cursor="pointer" />
        </Tooltip>
      ],
    }
  ];

  const rows: GridRowsProp = assets.map((asset) => (assetRow(asset)));
  return (
    <Box display="flex" flexDirection={"column"} gap={5}>
      <Box display="flex" flexDirection={"row"} justifyContent={"space-between"}>
        <Typography variant="h4" >Assets</Typography>
        <UnstyledLink to="add">
          <Button variant="contained">New</Button>
        </UnstyledLink>
      </Box>
      <Grid container spacing={2}>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({ 'compliant': 'pass' });
            setFilterModel({ items: [{ field: 'outcome', operator: 'is', value: 'Compliant' }] })
          }}>
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Compliant Assets</Typography>}
            right={<Typography fontSize={"1.25rem"} color={theme.palette.success.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
              <TaskAltIcon fontSize='inherit' /> {totalCompliant}
            </Typography>}
            bgcolor={theme.palette.successBackground.main}
            borderColor={theme.palette.successOutline.main}
            to={`/dashboard/${tenant_id}/assets?compliant=pass`}
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({ 'compliant': 'warning' });
            setFilterModel({ items: [{ field: 'outcome', operator: 'is', value: 'Warning' }] })
          }}
        >
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Assets With Warning</Typography>}
            right={<Typography fontSize={"1.25rem"} color={theme.palette.warning.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
              <ErrorOutlineIcon fontSize='inherit' /> {totalWarning}
            </Typography>}
            bgcolor={theme.palette.warningBackground.main}
            borderColor={theme.palette.warningOutline.main}
          />
        </Grid>
<Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({ 'compliant': 'fail' });
            setFilterModel({ items: [{ field: 'outcome', operator: 'is', value: 'Needs Attention' }] })
          }}
        >          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Assets Requiring Attention</Typography>}
            right={<Typography fontSize={"1.25rem"} color={theme.palette.error.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
              <ErrorOutlineIcon fontSize='inherit' /> {totalNonCompliant}
            </Typography>}
            bgcolor={theme.palette.errorBackground.main}
            borderColor={theme.palette.errorOutline.main}
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({});
            setFilterModel({ items: [] })
          }}
        >

          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Total Assets</Typography>}
            right={<Typography fontSize={"1.25rem"} color={theme.palette.primary.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
              <ErrorOutlineIcon fontSize='inherit' /> {totalCompliant + totalWarning + totalNonCompliant}
            </Typography>}
            bgcolor={theme.palette.primary.light}
            borderColor={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      <Box sx={{
        // mt: 5,
        width: '100%'
      }} >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            showToolbar
            initialState={{
              columns: {
                columnVisibilityModel: {
                  address_2: false,
                  city: true,
                  postcode: false,
                },
              }
            }}
            filterModel={filterModel}
            onFilterModelChange={(newFilterModel) => {
              setFilterModel(newFilterModel)

            }}
          />
        </div>
      </Box>
    </Box>
  )
}

export default Assets